import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';

import { useStudio, useUploadImage } from '../../queries';
import { ApiResponseError } from '../../api/envelope';
import type {
  ImageLabOperationType,
  OperationResult,
  ResultImageInfo,
  SourceImageRef,
} from '../../domain';
import {
  assetSummaryToResultImage,
  useSourceHandoffStore,
} from '../../store/sourceHandoffStore';

import { OperationDock, pickDefaultOperation } from './OperationDock';
import { CanvasSlot } from './canvas/CanvasSlot';
import { examplePromptsForStudio } from './examplePrompts';
import { useAgentResultStore } from '../../store/agentResultStore';
import { TopBar } from '../shared/topbar/TopBar';
import { HistoryPanel } from './history/HistoryPanel';
import type { HistoryEntryResponse } from '../../domain';
import { OPERATION_CATALOG } from './operationCatalog';
import { SwatchPanel } from '../shared/panels/SwatchPanel';
import { SelectionChips } from '../shared/panels/SelectionChips';
import { LabLoadingSkeleton } from '../shared/layout/LabLoadingSkeleton';
import { ActionPanel } from '../shared/panels/ActionPanel';
import { AgentBar } from '../shared/agent-bar/AgentBar';
import { FilterPanel } from './filters/FilterPanel';
import { DEFAULT_FILTERS_STATE, filtersToCss, isDefault as filtersIsDefault, resetFilters, type FiltersState } from './filters/filterState';
import { bakeFiltersToBlob, bakedFilterFilename } from './filters/bakeFilters';
import type { SwatchSelections } from '../shared/panels/swatchSelection';
import {
  buildStudioMetadata,
  describeSwatches,
  studioParamsFromSelections,
} from '../shared/panels/swatchSelection';
import { OperationPanel } from './operations';
import { StudioRequestContext } from './operations/studioRequestContext';
import { BakeOnSubmitContext } from './operations/bakeOnSubmitContext';
import type { BakeOnSubmitValue } from './operations/bakeOnSubmitContext';
import {
  appendResultImages,
  appendUploadedImage,
  sourceFromImage,
  uploadResponseToResultImage,
  useImageLabSession,
} from './hooks';
import type { CanvasMode } from './canvas/KonvaCanvas';
import type { ImageRect } from './canvas/selectionMath';
import type { Size } from './canvas/canvasMath';
import {
  DEFAULT_LEFT_RAIL_WIDTH,
  loadRailWidth,
  saveRailWidth,
} from '../shared/layout/railWidths';
import { RailSplitter } from '../shared/layout/RailSplitter';
import { useMobileShell } from '../../shell/useMobileShell';
import { MobileShell } from '../../shell/MobileShell';

/**
 * Per-kind canvas mode resolution. F12c-3 introduces two new gates:
 * SketchToImage swaps the canvas into 'sketch' (synthetic white tablet)
 * and Zoom swaps it into 'select' (drag-to-rect). Mask remains for
 * Inpaint / RemoveObject via the existing `requiresMask` flag.
 */
function resolveCanvasMode(kind: ImageLabOperationType | null): CanvasMode {
  if (!kind) return 'view';
  if (kind === 'SketchToImage') return 'sketch';
  if (kind === 'Zoom') return 'select';
  return OPERATION_CATALOG[kind]?.requiresMask ? 'mask' : 'view';
}

interface Props {
  studioId?: string;
}

/**
 * Image Lab shell per spec §4.3 + §9.1:
 *   Top bar (TODO F14) — Swatch | Canvas | Agent — Operation panel — Operation dock — Agent bar
 *
 * F12-session wires the session lifecycle (lazy create on first op submit
 * via `useImageLabSession`) and the source-image loop: each panel's
 * `onResult` lifts the produced `ResultImageInfo`s into local state, the
 * carousel surfaces them via `CanvasSlot`, and the `activeIndex`'s
 * `ResultImageInfo` is projected to a `SourceImageRef` for the next op.
 * The first Generate run is what populates the canvas; subsequent ops
 * (Inpaint, RemoveBackground) consume that output as source.
 */
export const ImageLab: React.FC<Props> = ({ studioId = 'generic' }) => {
  const { data: studio, isLoading, error } = useStudio(studioId);
  const { isMobileShell } = useMobileShell();
  const [activeKind, setActiveKind] = useState<ImageLabOperationType | null>(null);

  const { sessionId, ensureSession, isCreatingSession, sessionError } =
    useImageLabSession(studioId);
  const uploadMutation = useUploadImage();
  const [uploadError, setUploadError] = useState<Error | null>(null);

  const [images, setImages] = useState<ResultImageInfo[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Resizable rails — seed from localStorage (or defaults). RailSplitter
  // fires onCommit at pointer-up so we only write to storage per gesture,
  // not per mousemove tick.
  const [leftRailWidth, setLeftRailWidth] = useState(() => loadRailWidth('left'));
  const [rightRailWidth, setRightRailWidth] = useState(() => loadRailWidth('right'));

  const [maskDataUrl, setMaskDataUrl] = useState<string | null>(null);
  const [maskClearKey, setMaskClearKey] = useState(0);

  const [sketchDataUrl, setSketchDataUrl] = useState<string | null>(null);
  const [sketchClearKey, setSketchClearKey] = useState(0);

  const [selectionRect, setSelectionRect] = useState<ImageRect | null>(null);
  const [selectionClearKey, setSelectionClearKey] = useState(0);

  // Source-image dimensions (for Zoom's rect→params conversion). The
  // KonvaCanvas loads images internally; we mirror the active image's
  // intrinsic dims here so the panel builder has them.
  const [imageSize, setImageSize] = useState<Size | null>(null);

  // F14 swatch selections: paletteId -> active swatchId. F14b will lift
  // these into operation request DTOs; F14 just owns the selection UX.
  const [swatchSelections, setSwatchSelections] = useState<SwatchSelections>({});

  // Client-side CSS-filter preview state. Applied via the canvas
  // container's `filter` style — no BE round-trip. Resets on studio
  // switch alongside the rest of the canvas state.
  const [filters, setFilters] = useState<FiltersState>(DEFAULT_FILTERS_STATE);

  // Reset the canvas state when the studio switches so we don't carry a
  // Fashion session's outputs into Product Photography.
  useEffect(() => {
    setImages([]);
    setActiveIndex(0);
    setMaskDataUrl(null);
    setMaskClearKey(k => k + 1);
    setSketchDataUrl(null);
    setSketchClearKey(k => k + 1);
    setSelectionRect(null);
    setSelectionClearKey(k => k + 1);
    setImageSize(null);
    setSwatchSelections({});
    setFilters(DEFAULT_FILTERS_STATE);
  }, [studioId]);

  // F15-3: consume a pending "Use as source" handoff from the Asset
  // Gallery's preview modal. Subscribing reactively (vs `useEffect` on
  // studioId) handles the case where the user clicks "Use as source"
  // while already on ImageLab with the same studio — the route param
  // doesn't change, but the store does. Seeds the canvas with the
  // asset as if it were freshly uploaded; the server-side `assetId`
  // flows through `sourceFromImage` as `sourceImageId` for the next
  // operation.
  const pendingHandoff = useSourceHandoffStore(s => s.pending);
  useEffect(() => {
    if (!pendingHandoff) return;
    const consumed = useSourceHandoffStore.getState().consumeHandoff();
    if (!consumed) return;
    const seeded = assetSummaryToResultImage(consumed);
    setImages([seeded]);
    setActiveIndex(0);
  }, [pendingHandoff]);

  const defaultKind = useMemo(() => (studio ? pickDefaultOperation(studio) : null), [studio]);

  useEffect(() => {
    if (defaultKind && !activeKind) setActiveKind(defaultKind);
  }, [defaultKind, activeKind]);

  const canvasMode: CanvasMode = useMemo(() => resolveCanvasMode(activeKind), [activeKind]);

  const handleMaskChange = useCallback((next: string | null) => {
    setMaskDataUrl(next);
  }, []);

  const handleClearMask = useCallback(() => {
    setMaskClearKey(k => k + 1);
  }, []);

  const handleSketchChange = useCallback((next: string | null) => {
    setSketchDataUrl(next);
  }, []);

  const handleClearSketch = useCallback(() => {
    setSketchClearKey(k => k + 1);
  }, []);

  const handleSelectionChange = useCallback((next: ImageRect | null) => {
    setSelectionRect(next);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectionClearKey(k => k + 1);
  }, []);

  const handleResult = useCallback((result: OperationResult) => {
    setImages(prev => {
      const { images: next, firstNewIndex } = appendResultImages(prev, result);
      setActiveIndex(firstNewIndex);
      return next;
    });
  }, []);

  // F-history: when the user reverts / undoes / redoes via HistoryPanel,
  // sync the local carousel's activeIndex to the image referenced by the
  // history entry (or back to nothing for the blank initial state). If
  // the referenced image isn't in our local `images` list (page reload,
  // expired session state), leave activeIndex where it is — the user
  // sees the BE state updated but the local carousel keeps its prior
  // selection rather than jumping to a phantom position.
  const handleHistoryChange = useCallback(
    (entry: HistoryEntryResponse | null) => {
      if (!entry) return;
      setImages(prev => {
        const idx = prev.findIndex(i => i.id === entry.afterImageId);
        if (idx >= 0) setActiveIndex(idx);
        return prev;
      });
    },
    [],
  );

  // Toast shown after an auto-bake fires on i2i submit. The explicit
  // "Apply Filters" button doesn't surface one (the user just clicked it,
  // they know what happened). The auto-bake path does — the user hit
  // "Variations" expecting one round-trip and got two; the toast names
  // the side effect.
  const [bakeToast, setBakeToast] = useState<string | null>(null);

  /**
   * Load the active image, bake filters via offscreen canvas, upload via
   * the standard asset pipeline, append to the carousel, advance the
   * active index, reset filter sliders. Returns the adapted
   * `ResultImageInfo` for the freshly-uploaded baked asset.
   *
   * Used by both paths:
   *   - The FilterPanel "Apply Filters" button (`handleApplyFilters`) —
   *     fired manually by the user, no follow-on op.
   *   - The bake-on-submit auto-path (`bakeIfNeeded` below) — fired
   *     transparently when an i2i op submits with non-default filters,
   *     returning the ref so the op uses the baked source.
   *
   * Throws when the bake-or-upload pipeline fails. Callers should let the
   * error propagate to the user rather than silently downgrading to the
   * unfiltered source.
   */
  const bakeActiveImage = useCallback(async (): Promise<ResultImageInfo> => {
    const active = images[activeIndex];
    if (!active) {
      throw new Error('No active image to apply filters to.');
    }
    if (filtersIsDefault(filters)) {
      throw new Error('Filters are at their neutral state; nothing to bake.');
    }
    // Load the image fresh so the canvas isn't tainted. Browsers cache the
    // GET so this is typically a no-op network round-trip; the win is
    // having a fully-loaded HTMLImageElement to pass into bakeFiltersToBlob.
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.crossOrigin = 'anonymous';
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Could not load the source image to bake filters.'));
      el.src = active.signedUrl ?? active.url;
    });
    const baked = await bakeFiltersToBlob({
      source: {
        image: img,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
      },
      state: filters,
    });
    const sid = await ensureSession();
    const file = new File([baked.blob], bakedFilterFilename('filtered'), { type: baked.mimeType });
    // When the active image carries an asset id (the common case after
    // generate/upload/handoff per the `sourceFromImage` contract), tag the
    // bake upload with a `BakedFilters` lineage edge so the family graph
    // surfaces this asset as a derivative of the original. User-pasted URL
    // sources have no id, so the edge is skipped — we don't fabricate a
    // parent.
    const lineage = active.id
      ? { parentAssetId: active.id, relationshipType: 'BakedFilters' }
      : undefined;
    const response = await uploadMutation.mutateAsync({ file, sessionId: sid, lineage });
    const adapted = uploadResponseToResultImage(response);
    setImages(prev => {
      const { images: next, firstNewIndex } = appendUploadedImage(prev, adapted);
      setActiveIndex(firstNewIndex);
      return next;
    });
    // Reset filters to neutral so the just-baked image isn't double-filtered
    // on the preview.
    setFilters(resetFilters());
    return adapted;
  }, [images, activeIndex, filters, ensureSession, uploadMutation]);

  const handleApplyFilters = useCallback(async () => {
    await bakeActiveImage();
  }, [bakeActiveImage]);

  // Bake-on-submit value for the BakeOnSubmitContext. Wraps `bakeActiveImage`
  // with the "filters neutral? noop" early-out and surfaces the post-bake
  // toast. Returns the baked asset's `SourceImageRef` so
  // `useOperationMutation` can splice it onto the request's canvas-source
  // fields (sourceImage* / modelImage* depending on the op).
  //
  // Re-create the value object only when its function does — useMemo here
  // is just for referential stability of the context value, not the
  // function itself.
  const bakeIfNeeded = useCallback(async (): Promise<SourceImageRef | null> => {
    if (filtersIsDefault(filters)) return null;
    if (!images[activeIndex]) return null;
    const adapted = await bakeActiveImage();
    setBakeToast('Filters baked into a new layer before running the operation.');
    return sourceFromImage(adapted) ?? null;
  }, [filters, images, activeIndex, bakeActiveImage]);

  const bakeOnSubmitValue = useMemo<BakeOnSubmitValue>(
    () => ({ bakeIfNeeded }),
    [bakeIfNeeded],
  );

  const handleUploadFile = useCallback(
    async (file: File) => {
      setUploadError(null);
      try {
        const sid = await ensureSession();
        const response = await uploadMutation.mutateAsync({ file, sessionId: sid });
        const adapted = uploadResponseToResultImage(response);
        setImages(prev => {
          const { images: next, firstNewIndex } = appendUploadedImage(prev, adapted);
          setActiveIndex(firstNewIndex);
          return next;
        });
      } catch (err) {
        setUploadError(err instanceof Error ? err : new Error('Upload failed.'));
      }
    },
    [ensureSession, uploadMutation],
  );

  // Prefer signedUrl over url so KonvaCanvas's `new Image(); img.src = …`
  // load works without the `x-user-id` header the browser can't attach.
  // See qa/bugs/2026-05-25-asset-files-401-canvas-render.md.
  const imageUrls = useMemo(() => images.map(i => i.signedUrl ?? i.url), [images]);
  const source = useMemo(() => sourceFromImage(images[activeIndex]), [images, activeIndex]);
  const studioParams = useMemo(
    () => (studio ? studioParamsFromSelections(studio.parameterPalettes, swatchSelections) : undefined),
    [studio, swatchSelections],
  );
  // Bracket-describe path stays for back-compat with non-Generic Studios whose
  // palettes use kinds the structured projection doesn't recognize. Strictly
  // empty for Generic Studio (its three kinds are all in the recognized set).
  const swatchPrompt = useMemo(
    () => (studio ? describeSwatches(studio.parameterPalettes, swatchSelections) : ''),
    [studio, swatchSelections],
  );

  // Studio-typed metadata to ride along with every operation (Path C Slice 6).
  // Only tag the op (studioId + metadata) when the swatch selections actually
  // map to schema fields — otherwise operations run exactly as before.
  const studioRequest = useMemo(() => {
    const metadata = studio
      ? buildStudioMetadata(studio.metadataSchema, studio.parameterPalettes, swatchSelections)
      : undefined;
    return metadata ? { studioId: studio?.id, metadata } : {};
  }, [studio, swatchSelections]);

  // Mirror the active image's intrinsic dims into imageSize so the Zoom
  // panel can normalize a selection rect into BE-expected fractions.
  // ResultImageInfo carries width/height; no need to re-load the image.
  useEffect(() => {
    const active = images[activeIndex];
    if (active && active.width > 0 && active.height > 0) {
      setImageSize({ width: active.width, height: active.height });
    } else {
      setImageSize(null);
    }
  }, [images, activeIndex]);

  // Empty-canvas example chip → seed the Generate prompt (via the existing
  // agent-result handoff that GeneratePanel already consumes) and switch to
  // the Generate operation so the seeded prompt is visible.
  const handleExamplePrompt = useCallback((prompt: string) => {
    useAgentResultStore
      .getState()
      .setHandoff({ agentId: 'examples', agentName: 'Example prompt', promptText: prompt });
    setActiveKind('TextToImage');
  }, []);

  if (isLoading) {
    return <LabLoadingSkeleton />;
  }

  if (error || !studio) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          {error instanceof ApiResponseError
            ? `${error.code}: ${error.message}`
            : error instanceof Error
              ? error.message
              : `Studio "${studioId}" not found.`}
        </Alert>
      </Box>
    );
  }

  // Shared content nodes — same components in both shells. Spike 1 polish
  // (per `creative-toolbox-v3-memory/17-mobile-shell-plan-2026-05-26.md` §3):
  // mobile slots them into bottom-tab views; desktop arranges them as fixed
  // left/right rails.
  const swatchesContent = (
    <>
      <SwatchPanel
        palettes={studio.parameterPalettes}
        selections={swatchSelections}
        onSelectionsChange={setSwatchSelections}
        studioId={studio.id}
      />
      <FilterPanel
        state={filters}
        onChange={setFilters}
        hasSourceImage={images.length > 0}
        onApply={images.length > 0 ? handleApplyFilters : undefined}
      />
      <HistoryPanel sessionId={sessionId} onHistoryChange={handleHistoryChange} />
    </>
  );

  const canvasContent = (
    <CanvasSlot
      images={imageUrls}
      activeIndex={activeIndex}
      onActiveIndexChange={setActiveIndex}
      mode={canvasMode}
      onMaskChange={handleMaskChange}
      maskClearKey={maskClearKey}
      onSketchChange={handleSketchChange}
      sketchClearKey={sketchClearKey}
      onSelectionChange={handleSelectionChange}
      selectionClearKey={selectionClearKey}
      onUploadFile={handleUploadFile}
      isUploading={uploadMutation.isPending}
      emptyHint="Drop an image here, click upload, or describe what you want to create."
      previewFilterCss={filtersToCss(filters)}
      examplePrompts={examplePromptsForStudio(studioId)}
      onExamplePrompt={handleExamplePrompt}
    />
  );

  const operationsContent = (
    <>
      <SelectionChips
        palettes={studio.parameterPalettes}
        selections={swatchSelections}
        onSelectionsChange={setSwatchSelections}
      />
      {activeKind && (
        <Box sx={{ p: 1 }}>
          <StudioRequestContext.Provider value={studioRequest}>
            <BakeOnSubmitContext.Provider value={bakeOnSubmitValue}>
              <OperationPanel
                kind={activeKind}
                ensureSession={ensureSession}
                isCreatingSession={isCreatingSession}
                source={source}
                maskDataUrl={maskDataUrl}
                onClearMask={handleClearMask}
                sketchDataUrl={sketchDataUrl}
                onClearSketch={handleClearSketch}
                selection={selectionRect}
                onClearSelection={handleClearSelection}
                imageSize={imageSize}
                swatchPrompt={swatchPrompt}
                studioParams={studioParams}
                onResult={handleResult}
              />
            </BakeOnSubmitContext.Provider>
          </StudioRequestContext.Provider>
        </Box>
      )}
      {activeKind && (
        <Box sx={{ mt: 'auto', borderTop: theme => `1px solid ${theme.palette.divider}` }}>
          <OperationDock studio={studio} activeKind={activeKind} onSelect={setActiveKind} />
        </Box>
      )}
    </>
  );

  // Elevation pass 2026-05-26 (Area 3) — feed the chat surface the live
  // canvas state so the LLM can ground its responses in what the user is
  // actually working on (swatch picks, filter slider state, active
  // carousel image). The BE `ChatController` formats these into a system-
  // prompt prelude when present.
  const activeAssetId = images[activeIndex]?.id;
  const agentBar = (
    <AgentBar
      sessionId={sessionId}
      labId={studio.labId}
      labName={studio.labId === 'image-lab' ? 'Image Lab' : studio.labId}
      studioId={studioId}
      studioName={studio.name}
      activeOperationKind={activeKind ?? undefined}
      swatchSelections={swatchSelections}
      // FiltersState is a struct with named slider keys; the AgentBar's
      // prop type is `Record<string, number>` so it can also serve labs
      // with different slider shapes. Spread coerces the struct into the
      // wider index-signature shape without a cast.
      filterState={{ ...filters }}
      activeAssetId={activeAssetId}
      ensureImageSession={ensureSession}
    />
  );

  const bakeSnackbar = (
    <Snackbar
      open={!!bakeToast}
      autoHideDuration={4000}
      onClose={() => setBakeToast(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity="info"
        variant="filled"
        onClose={() => setBakeToast(null)}
        sx={{ width: '100%' }}
      >
        {bakeToast}
      </Alert>
    </Snackbar>
  );

  const sessionErrorAlert = sessionError && (
    <Alert severity="error" onClose={() => undefined} sx={{ m: 1 }}>
      {`Couldn't start a session: ${sessionError.message}`}
    </Alert>
  );
  const uploadErrorAlert = uploadError && (
    <Alert severity="error" onClose={() => setUploadError(null)} sx={{ m: 1 }}>
      {`Upload failed: ${uploadError.message}`}
    </Alert>
  );

  if (isMobileShell) {
    // Mobile shell — bottom-tab navigation. The 5 heavy ops (Inpaint, Outpaint,
    // RemoveObject, SketchToImage, TryOn) are deferred via the HeavyCreationOverlay
    // mounted inside OperationPanel (per spike §1.5). Other ops are full-featured
    // on mobile light-edit per the Mode Commitment Doc.
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        {sessionErrorAlert}
        {uploadErrorAlert}
        <MobileShell
          title={`${studio.labId === 'image-lab' ? 'Image Lab' : studio.labId} · ${studio.name}`}
          canvasSlot={
            <Box sx={{ flex: 1, display: 'flex', minHeight: 0, p: 1 }}>
              {canvasContent}
            </Box>
          }
          swatchesSlot={
            <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {swatchesContent}
            </Box>
          }
          actionSlot={operationsContent}
          agentBarSlot={agentBar}
        />
        {bakeSnackbar}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <TopBar labId={studio.labId} studioId={studioId} />
      {sessionErrorAlert}
      {uploadErrorAlert}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Left rail — preview-time helpers (v3 UI spec §4.3 revised).
            Width is user-resizable via the RailSplitter; persisted to
            localStorage per gesture. */}
        <Box
          sx={{
            width: leftRailWidth,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRight: theme => `1px solid ${theme.palette.divider}`,
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          {swatchesContent}
        </Box>

        <RailSplitter
          side="left"
          width={leftRailWidth}
          onChange={setLeftRailWidth}
          onCommit={w => saveRailWidth('left', w)}
        />

        {/* Center — canvas only. */}
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0, p: 1 }}>
          {canvasContent}
        </Box>

        <RailSplitter
          side="right"
          width={rightRailWidth}
          onChange={setRightRailWidth}
          onCommit={w => saveRailWidth('right', w)}
        />

        {/* Right rail — primary action space (Operations + Agents tabs). */}
        <ActionPanel
          studio={studio}
          width={rightRailWidth}
          operationsContent={operationsContent}
        />
      </Box>
      {agentBar}
      {bakeSnackbar}
    </Box>
  );
};
